// sections/SectionPricing.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
  Button,
  TextField,
  Grid,
  Alert,
  InputAdornment,
} from "@mui/material";

function SectionCard({ title, subtitle, right, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {right}
        </Stack>
        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.25)" }} />
        {children}
      </CardContent>
    </Card>
  );
}

const whiteField = {
  InputLabelProps: { sx: { color: "#fff" } },
  FormHelperTextProps: { sx: { color: "rgba(255,255,255,0.75)" } },
  sx: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
      "&.Mui-focused fieldset": { borderColor: "#fff" },
    },
    "& .MuiSvgIcon-root": { color: "#fff" },
  },
};

export default function SectionPricing({
  form,
  setField,
  setNested,
  updatePricingSection, // ✅ new
  saving,
  dirty,
  onSaved,
}) {
  const { t } = useTranslation();
  const save = async () => {
    const payload = {
      notifyBefore: form.notifyBefore || "",
      price: {
        hour: form.price?.hour === "" ? undefined : Number(form.price.hour),
        day: form.price?.day === "" ? undefined : Number(form.price.day),
        event: form.price?.event === "" ? undefined : Number(form.price.event),
      },
    };

    const ok = await updatePricingSection(payload);
    if (ok) onSaved?.();
  };

  return (
    <SectionCard
      title={t("info.pricing.sectionTitle")}
      subtitle={t("info.pricing.subtitle")}
      right={
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      <Stack spacing={2}>
        <TextField
          label={t("info.pricing.notifyBefore")}
          value={form.notifyBefore || ""}
          onChange={(e) => setField("notifyBefore", e.target.value)}
          fullWidth
          helperText={t("info.pricing.notifyBeforeHint")}
          {...whiteField}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label={t("info.pricing.perHour")}
              value={form.price?.hour ?? ""}
              onChange={(e) => setNested("price", "hour", e.target.value.replace(/[^\d.]/g, ""))}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: "#fff !important", fontWeight: 600 }}>
                      €
                    </Typography>
                  </InputAdornment>
                ),
              }}
              {...whiteField}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t("info.pricing.perDay")}
              value={form.price?.day ?? ""}
              onChange={(e) => setNested("price", "day", e.target.value.replace(/[^\d.]/g, ""))}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: "#fff !important", fontWeight: 600 }}>
                      €
                    </Typography>
                  </InputAdornment>
                ),
              }}
              {...whiteField}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t("info.pricing.perEvent")}
              value={form.price?.event ?? ""}
              onChange={(e) => setNested("price", "event", e.target.value.replace(/[^\d.]/g, ""))}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: "#fff !important", fontWeight: 600 }}>
                      €
                    </Typography>
                  </InputAdornment>
                ),
              }}
              {...whiteField}
            />
          </Grid>
        </Grid>

        <Alert
          severity="warning"
          sx={{
            bgcolor: "rgba(255,255,255,0.08)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.25)",
            "& .MuiAlert-icon": { color: "#fff" },
          }}
        >
          {t("info.pricing.emptyNote")}
        </Alert>
      </Stack>
    </SectionCard>
  );
}